import { PropsWithChildren, useEffect } from 'react'
import useHttpLoader from '../../../shared/hooks/useHttpLoader.ts'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import equipmentAtom from '../../Equipment/atoms/equipment.atom.ts'
import mainApi from '../main.api.ts'
import ThreeDotsLoader from '../../../shared/Loaders/ThreeDotsLoader'
import css from './contentLoaderGate.module.css'
import parametersAtom from '../../Parameters/parameters.atom.ts'
import authAtom from '../../Auth/auth.atom.ts'
import maintenanceBlankSchemasAtom from '../../Processes/Maintenance/MaintenanceBlanks/maintenanceBlankSchemas.atom.ts'
import maintenanceProcessesAtom from '../../Processes/Maintenance/MaintenanceProcesses/maintenanceProcesses.atom.ts'
import equipmentModelsAtom from '../../Equipment/pages/EquipmentModels/equipmentModels.atom.ts'
import parameterGroupsAtom from '@/pages/Parameters/parameterGroups.atom.ts'
import rolesAtom from '@/pages/Users/roles.atom.ts'
import userAtom from '@/pages/User/user.atom.ts'
import enumsAtom from '@/pages/MDM/atoms/enums.atom.ts'
import unitsAtom from '@/pages/MDM/atoms/units.atom.ts'
import unitInstancesAtom from '@/pages/MDM/atoms/units.instances.atom.ts'
import passportSchemasAtom from '@/pages/MDM/atoms/passportSchemas.atom.ts'
import protocolsAtom from '@/pages/Equipment/atoms/protocols.atom.ts'
import organizationsAtom from '@/pages/Organizations/organizations.atom.ts'
import alarmSituationsAtom from '@/pages/Processes/AlarmSituations/alarmSituations.atom.ts'
import dashboardsAtom from '@/pages/Dashboards/dashboards.atom.ts'
import dashboardGroupsAtom from '@/pages/Dashboards/dashboard.groups.atom.ts'
import favouriteEquipmentAtom from '@/pages/User/atoms/favouriteEquipment.atom.ts'
import useSetSelfOrganization from '@/pages/MyOrganization/hooks/useSetSelfOrganization.ts'

const ContentLoadedGate = (props: PropsWithChildren) => {
  const isLoaded = useRecoilValue(equipmentAtom).loaded
  const { wait } = useHttpLoader()

  const authState = useRecoilValue(authAtom)
  const setEquipmentState = useSetRecoilState(equipmentAtom)
  const setProtocolsState = useSetRecoilState(protocolsAtom)
  const setFavouriteEquipments = useSetRecoilState(favouriteEquipmentAtom)
  const setEquipmentModelsState = useSetRecoilState(equipmentModelsAtom)
  const setEnumsState = useSetRecoilState(enumsAtom)
  const setMDMUnitsState = useSetRecoilState(unitsAtom)
  const setMDMUnitInstancesState = useSetRecoilState(unitInstancesAtom)
  const setMDMPassportsState = useSetRecoilState(passportSchemasAtom)
  const setOrganizationsState = useSetRecoilState(organizationsAtom)

  const setAlarmSituationsState = useSetRecoilState(alarmSituationsAtom)
  const setParametersState = useSetRecoilState(parametersAtom)
  const setParameterGroupsState = useSetRecoilState(parameterGroupsAtom)
  const setMaintenanceBlankSchemasState = useSetRecoilState(maintenanceBlankSchemasAtom)
  const setMaintenanceProcessesState = useSetRecoilState(maintenanceProcessesAtom)
  const setRolesState = useSetRecoilState(rolesAtom)
  const setUserState = useSetRecoilState(userAtom)

  const setDashboardsState = useSetRecoilState(dashboardsAtom)
  const setDashboardGroupsState = useSetRecoilState(dashboardGroupsAtom)
  const setSelfOrganization = useSetSelfOrganization()

  useEffect(() => {
    if (isLoaded || authState.authState !== 'authorized') return
    wait(mainApi.initData(), (resp) => {
      if (resp.status === 'success') {
        setEquipmentState({ loaded: true, items: resp.body.equipment.items })
        setEquipmentModelsState((prev) => ({ ...prev, items: resp.body.equipment.models }))
        setProtocolsState((prev) => ({ ...prev, items: resp.body.equipment.protocols }))
        setFavouriteEquipments((prev) => ({ ...prev, items: resp.body.equipment.favourite }))
        setEnumsState((prev) => ({ ...prev, items: resp.body.mdm.enums }))
        setMDMUnitsState((prev) => ({ ...prev, items: resp.body.mdm.units }))
        setMDMUnitInstancesState((prev) => ({ ...prev, items: resp.body.mdm.unitInstances }))
        setMDMPassportsState((prev) => ({ ...prev, items: resp.body.mdm.passportSchemas }))
        setParametersState((prev) => ({
          ...prev,
          items: resp.body.parameters.items,
          unitPrefixes: resp.body.unitPrefixes,
          units: resp.body.units,
        }))
        setMaintenanceBlankSchemasState((prev) => ({ ...prev, items: resp.body.maintenance.blankSchemas }))
        setMaintenanceProcessesState((prev) => ({ ...prev, items: resp.body.maintenance.processes }))
        setParameterGroupsState((prev) => ({ ...prev, items: resp.body.parameters.groups }))
        setRolesState((prev) => ({ ...prev, items: resp.body.roles }))
        setUserState({
          ...resp.body.user,
          accessRights: resp.body.selfAccessRights,
          isNotificationsViewed: resp.body.unseenNotifications.length === 0,
        })
        setOrganizationsState((prev) => ({ ...prev, items: resp.body.organizations }))
        setAlarmSituationsState((prev) => ({ ...prev, items: resp.body.alarmSituations }))

        setDashboardGroupsState((prev) => ({ ...prev, items: resp.body.dashboards.groups }))
        setDashboardsState((prev) => ({ ...prev, items: resp.body.dashboards.items }))
        setSelfOrganization(resp.body.userOrganization)
      }
    })
  }, [authState.authState])

  if (!isLoaded) {
    return (
      <div className={css.wrapper}>
        <ThreeDotsLoader inscription="Инициализация..." />
      </div>
    )
  }

  return <>{props.children}</>
}

export default ContentLoadedGate
